import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useHousehold } from '../hooks/useHousehold';

function AppLogo() {
  return (
    <>
      <img src="/dinner-league-icon-192.png" alt="" aria-hidden="true" className="w-20 h-20 mb-4 rounded-2xl shadow-glow-sm" />
      <h1 className="font-display text-3xl font-semibold text-ink mb-1">Suppertide</h1>
    </>
  );
}

function MemberTypeToggle({ value, onChange }) {
  return (
    <div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange('adult')}
          className={`flex-1 py-2.5 rounded-xl font-body font-medium text-sm transition-colors ${
            value === 'adult'
              ? 'bg-lime text-canvas'
              : 'bg-surface text-ink-soft border border-hairline'
          }`}
        >
          Adult
        </button>
        <button
          type="button"
          onClick={() => onChange('kid')}
          className={`flex-1 py-2.5 rounded-xl font-body font-medium text-sm transition-colors ${
            value === 'kid'
              ? 'bg-lime text-canvas'
              : 'bg-surface text-ink-soft border border-hairline'
          }`}
        >
          Kid
        </button>
      </div>
      <p className="font-body text-xs text-ink-muted mt-2 leading-relaxed">
        Adults manage the household and plans. Kids (under 13) get a simpler experience an adult sets up.
      </p>
    </div>
  );
}

function ChoiceScreen({ onChoose }) {
  const { signOut } = useAuth();

  return (
    <>
      <AppLogo />
      <p className="font-body text-ink-soft mb-8">Set up your household to get started.</p>

      <div className="w-full space-y-3">
        <button
          onClick={() => onChoose('create')}
          className="w-full bg-panel rounded-2xl p-5 border border-hairline text-left hover:border-lime/40 transition-colors"
        >
          <div className="font-body font-bold text-ink-soft mb-1">Create a household</div>
          <div className="font-body text-sm text-ink-soft">Start fresh. Invite your family later.</div>
        </button>

        <button
          onClick={() => onChoose('join')}
          className="w-full bg-panel rounded-2xl p-5 border border-hairline text-left hover:border-lime/40 transition-colors"
        >
          <div className="font-body font-bold text-ink-soft mb-1">Join a household</div>
          <div className="font-body text-sm text-ink-soft">Have an invite code? Enter it here.</div>
        </button>
      </div>

      <button
        onClick={signOut}
        className="mt-6 font-body text-sm text-ink-muted hover:text-ink-soft transition-colors"
      >
        Sign out
      </button>
    </>
  );
}

function CreateForm({ onBack }) {
  const { refreshMembership } = useAuth();
  const { createHousehold, loading } = useHousehold();
  const navigate = useNavigate();
  const [householdName, setHouseholdName] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberType, setMemberType] = useState('adult');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const { error: rpcError } = await createHousehold({
      householdName: householdName.trim(),
      memberName: memberName.trim(),
      memberType,
    });

    if (rpcError) {
      setError(rpcError.message);
    } else {
      await refreshMembership();
      // Land new players on the iconic swipe gesture, not the home grid.
      navigate('/swipe', { replace: true });
    }
  };

  return (
    <>
      <AppLogo />
      <p className="font-body text-ink-soft mb-6">Create your household</p>

      <form onSubmit={handleSubmit} className="w-full bg-panel rounded-2xl p-6 border border-hairline">
        <label htmlFor="householdName" className="block font-body text-sm font-medium text-ink-soft mb-1.5">
          Household name
        </label>
        <input
          id="householdName"
          type="text"
          required
          value={householdName}
          onChange={(e) => setHouseholdName(e.target.value)}
          placeholder="The Smiths"
          className="w-full px-4 py-3 rounded-xl border border-hairline bg-surface font-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent transition-shadow mb-4"
        />

        <label htmlFor="memberName" className="block font-body text-sm font-medium text-ink-soft mb-1.5">
          Your name
        </label>
        <input
          id="memberName"
          type="text"
          required
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
          placeholder="Alex"
          className="w-full px-4 py-3 rounded-xl border border-hairline bg-surface font-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent transition-shadow mb-4"
        />

        <label className="block font-body text-sm font-medium text-ink-soft mb-1.5">
          I am a...
        </label>
        <MemberTypeToggle value={memberType} onChange={setMemberType} />

        {error && (
          <p className="mt-3 font-body text-sm text-red-600">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full mt-5 py-3 rounded-xl bg-lime hover:bg-lime active:bg-lime text-canvas font-body font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Create household'}
        </button>
      </form>

      <button
        onClick={onBack}
        className="mt-4 font-body text-sm text-ink-muted hover:text-ink-soft transition-colors"
      >
        Back
      </button>
    </>
  );
}

function JoinForm({ onBack, initialCode }) {
  const { refreshMembership } = useAuth();
  const { joinHousehold, loading } = useHousehold();
  const navigate = useNavigate();
  const [code, setCode] = useState(initialCode || '');
  const [memberName, setMemberName] = useState('');
  const [memberType, setMemberType] = useState('adult');
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const { error: rpcError } = await joinHousehold({
      // Invite codes are case-sensitive (mixed-case alphabet) and matched
      // exactly server-side — do NOT lowercase, or codes with uppercase
      // letters will never match.
      code: code.trim(),
      memberName: memberName.trim(),
      memberType,
    });

    if (rpcError) {
      if (rpcError.message.includes('Invalid invite code')) {
        setError('That invite code doesn\u2019t match any household. Double-check and try again.');
      } else {
        setError(rpcError.message);
      }
    } else {
      await refreshMembership();
      // Land new players on the iconic swipe gesture, not the home grid.
      navigate('/swipe', { replace: true });
    }
  };

  return (
    <>
      <AppLogo />
      <p className="font-body text-ink-soft mb-6">Join an existing household</p>

      <form onSubmit={handleSubmit} className="w-full bg-panel rounded-2xl p-6 border border-hairline">
        <label htmlFor="inviteCode" className="block font-body text-sm font-medium text-ink-soft mb-1.5">
          Invite code
        </label>
        <input
          id="inviteCode"
          type="text"
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="abc12def"
          className="w-full px-4 py-3 rounded-xl border border-hairline bg-surface font-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent transition-shadow font-monor mb-4"
        />

        <label htmlFor="joinMemberName" className="block font-body text-sm font-medium text-ink-soft mb-1.5">
          Your name
        </label>
        <input
          id="joinMemberName"
          type="text"
          required
          value={memberName}
          onChange={(e) => setMemberName(e.target.value)}
          placeholder="Alex"
          className="w-full px-4 py-3 rounded-xl border border-hairline bg-surface font-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-lime focus:border-transparent transition-shadow mb-4"
        />

        <label className="block font-body text-sm font-medium text-ink-soft mb-1.5">
          I am a...
        </label>
        <MemberTypeToggle value={memberType} onChange={setMemberType} />

        {error && (
          <p className="mt-3 font-body text-sm text-red-600">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full mt-5 py-3 rounded-xl bg-lime hover:bg-lime active:bg-lime text-canvas font-body font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Joining...' : 'Join household'}
        </button>
      </form>

      <button
        onClick={onBack}
        className="mt-4 font-body text-sm text-ink-muted hover:text-ink-soft transition-colors"
      >
        Back
      </button>
    </>
  );
}

export default function HouseholdSetup({ initialCode }) {
  const [step, setStep] = useState(initialCode ? 'join' : 'choice'); // 'choice' | 'create' | 'join'
  const [codeFromLink, setCodeFromLink] = useState(initialCode);

  const handleBack = () => {
    setStep('choice');
    setCodeFromLink(undefined);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-canvas to-canvas flex justify-center">
      <div className="w-full max-w-md flex flex-col items-center justify-center px-6">
        {step === 'choice' && <ChoiceScreen onChoose={setStep} />}
        {step === 'create' && <CreateForm onBack={handleBack} />}
        {step === 'join' && <JoinForm onBack={handleBack} initialCode={codeFromLink} />}
      </div>
    </div>
  );
}
